import * as React from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { notification, Modal, Input } from "antd";
import { parentLink, nameOfKind, request, getName, pluralNameOfKind } from "../../services/cpln";
import { Kind } from "../../mst/base";
import { UserData } from "../../mobxStores/userData/userData";
import { NGButton } from "../../newcomponents/button/Button";
import { PromptContext } from "../../mobxStores/prompt/prompt";
import { isApex, sleep } from "../../services/utils";

interface Props {
  kind: string;
  itemName?: string;
  links: string[];
  onCancel: any;
  onComplete: any;
}

const DeleteModalRaw: React.FC<Props> = ({ kind, itemName, links, onCancel, onComplete }) => {
  const navigate = useNavigate();

  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    if (itemName) {
      return itemName;
    }
    return `Selected ${pluralNameOfKind(kind)}`;
  }, [itemName, kind]);

  async function confirmDelete() {
    try {
      setIsLoading(true);
      const navigation = parentLink(kind as Kind);
      const promisesBatch1: Promise<any>[] = [];
      const promisesBatch2: Promise<any>[] = [];
      if (kind === "domain") {
        const apexDomains: string[] = [];
        const subDomains: string[] = [];
        for (const link of links) {
          const name = link.split(`/${kind}/`)[1];
          if (isApex(name)) {
            apexDomains.push(link);
          } else {
            subDomains.push(link);
          }
        }
        for (const link of subDomains) {
          promisesBatch1.push(request({ method: "delete", url: link }));
        }
        for (const link of apexDomains) {
          promisesBatch2.push(request({ method: "delete", url: link }));
        }
      } else {
        for (const link of links) {
          promisesBatch1.push(request({ method: "delete", url: link }));
        }
      }
      await Promise.all(promisesBatch1);
      if (promisesBatch2.length > 0) {
        await sleep(2);
        await Promise.all(promisesBatch1);
      }
      if (kind === "gvc" || kind === "workload") {
        await UserData.requestHasWorkload();
      }
      notification.success({
        message: "Success",
        description: `Deleted Item${links.length > 1 ? "s" : ""}`,
      });
      onComplete();
      PromptContext.setIsDisabled(true);
      navigate(`/console${navigation}`);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      const hasDetails = e?.response?.data?.details?.usedBy && e.response.data.details.usedBy.length > 0;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to delete",
        description: hasDetails ? (
          <div>
            <span>{errorMessage}</span>
            <ul>
              {e.response.data.details.usedBy.map((link: string) => (
                <li className="cursor-pointer  " onClick={() => navigate(`/console${link}`)}>
                  {link}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <span>{errorMessage}</span>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  }

  let shouldConfirmDeletion = false;
  let canDelete = true;
  if (["volumeset", "workload", "identity", "mk8s", "secret"].includes(kind)) {
    shouldConfirmDeletion = true;
    if (confirmationText !== CONFIRMATION_TOKEN) {
      canDelete = false;
    }
  }

  return (
    <Modal
      title={
        <>
          Delete {nameOfKind(kind)}:{" "}
          <span className="font-bold" style={{ color: "var(--color-danger)" }}>
            {links.length < 2 ? getName(links[0]) : `${links.length} Items`}
          </span>
        </>
      }
      open={true}
      closable={false}
      maskClosable={!isLoading}
      onCancel={onCancel}
      footer={
        <div className="modal-actions">
          <NGButton variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </NGButton>
          <NGButton variant="danger" onClick={confirmDelete} loading={isLoading} disabled={isLoading || !canDelete}>
            Delete
          </NGButton>
        </div>
      }
      destroyOnClose
    >
      <div className="flex flex-col gap-2">
        <div>
          Are you sure you want to delete {links.length > 1 ? "these" : "this"}{" "}
          {links.length > 1 ? pluralNameOfKind(kind) : nameOfKind(kind)}? This action cannot be undone.
        </div>
        {shouldConfirmDeletion ? (
          <>
            <div className="delete-modal-confirmation-warning mt-4">{`Type "${CONFIRMATION_TOKEN}" for confirming the delete action`}</div>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={`confirmation`}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export const DeleteModal = observer(DeleteModalRaw);
